import { useEffect, useState } from "react";
import { Link, Route, Switch, useLocation } from "wouter";
import { apiGet } from "./api";
import { detectPlatformKey, getTelegramWebApp, useTelegramBackButton, useTelegramTheme } from "./telegram";
import { HomeScreen } from "./components/HomeScreen";
import { InstallGuide } from "./components/InstallGuide";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import type { SubscriptionViewModel } from "../subscriptionView";

export function App() {
  const [me, setMe] = useState<SubscriptionViewModel | null>(null);
  const [locale, setLocale] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [location] = useLocation();

  useTelegramBackButton();
  useTelegramTheme();

  useEffect(() => {
    apiGet<SubscriptionViewModel>("/api/me")
      .then((data) => {
        setMe(data);
        const tgLanguage = getTelegramWebApp()?.initDataUnsafe.user?.language_code;
        setLocale(tgLanguage && data.locales.includes(tgLanguage) ? tgLanguage : data.defaultLocale);
      })
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <div className="max-w-[640px] mx-auto p-4">
        <p className="text-accent-red">Пользователь не найден. Обратитесь к администратору.</p>
      </div>
    );
  }

  if (!me || !locale) {
    return (
      <div className="max-w-[640px] mx-auto p-4">
        <p className="text-text-muted">…</p>
      </div>
    );
  }

  const localeData = me.data[locale];
  const labels = localeData?.labels ?? {};
  const navLinkClass = (active: boolean) =>
    `${active ? "text-accent-green" : "text-fg"} hover:opacity-80 transition-opacity duration-150`;

  return (
    <div className="max-w-[640px] mx-auto p-4">
      <header className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <nav className="flex gap-4">
          <Link href="/" className={navLinkClass(location === "/")}>
            [{labels.subscriptionTab}]
          </Link>
          <Link href="/install" className={navLinkClass(location === "/install")}>
            [{labels.installTab}]
          </Link>
        </nav>
        <LanguageSwitcher locales={me.locales} locale={locale} onChange={setLocale} />
      </header>
      <main>
        <Switch>
          <Route path="/">
            <HomeScreen me={me} labels={labels} />
          </Route>
          <Route path="/install">
            <InstallGuide
              platforms={localeData?.platforms ?? []}
              labels={labels}
              defaultPlatformKey={detectPlatformKey()}
            />
          </Route>
        </Switch>
      </main>
    </div>
  );
}
